import { Button, Switch, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { boolean, number, object, string } from 'yup';
import { useCreateUser } from '~/api/users';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';

const validationSchema = (t: (key: string) => string) =>
  object({
    name: string().required(t('common.labels.name_required')),
    email: string()
      .email(t('common.labels.email_validation'))
      .required(t('common.labels.email_required')),
    owner: boolean().required(),
    networkId: number().required(),
  });

export const NewUserDialog = (props: DialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const navigate = useNavigate();
  const link = useLink();

  const [createUser] = useCreateUser();

  // Sucks
  const close = () => props.onClose?.({}, 'backdropClick');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      owner: false,
      networkId: currentNetwork.id,
    },
    onSubmit: async (values) => {
      const result = await createUser({
        variables: { input: values },
      });
      navigate(link(`/users/${result.data?.createUser?.user?.id}`));
    },
    validateOnBlur: true,
    validationSchema: validationSchema(t),
  });

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.users.dialogs.new_user.title')}</DialogTitle>

      <DialogContent>
        <DialogForm id="new-user" onSubmit={formik.handleSubmit}>
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>

          <label>
            <span className={`label required`}>{t('common.labels.email')}</span>
            <TextField
              error={formik.touched.email && !!formik.errors.email}
              fullWidth
              helperText={(formik.touched.email && formik.errors.email) || ' '}
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.email}
            />
          </label>

          <label>
            <span className={`label`}>{t('pages.users.dialogs.new_user.owner')}</span>
            <Switch
              checked={formik.values.owner}
              color="primary"
              name="owner"
              onChange={formik.handleChange}
            />
          </label>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.buttons.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          form="new-user"
          type="submit"
          variant="contained"
        >
          {t('common.buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
